import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { providerService } from '../services/providerService';
import { entitySyncService } from '../services/entitySyncService';

// Async thunks for provider operations
// (No changes needed in this section)

export const fetchProviders = createAsyncThunk(
  'providers/fetchProviders',
  async (query, { rejectWithValue }) => {
    try {
      const response = await providerService.fetchProviders(query);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const loadProvidersFromCache = createAsyncThunk(
  'providers/loadFromCache',
  async (_, { rejectWithValue }) => {
    try {
      const data = await entitySyncService.getLocalEntities('providers');
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createProvider = createAsyncThunk(
  'providers/createProvider',
  async (providerData, { rejectWithValue }) => {
    try {
      const response = await providerService.createProvider(providerData);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateProvider = createAsyncThunk(
  'providers/updateProvider',
  async ({ providerId, data }, { rejectWithValue }) => {
    try {
      const response = await providerService.updateProvider(providerId, data);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteProvider = createAsyncThunk(
  'providers/deleteProvider',
  async (id, { rejectWithValue }) => {
    try {
      await providerService.deleteProvider(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchProviderById = createAsyncThunk(
  'providers/fetchProviderById',
  async (payload, { rejectWithValue }) => {
    const id = typeof payload === 'object' ? payload.id : payload;
    try {
      const response = await providerService.getProviderById(id);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
  {
    condition: (payload, { getState }) => {
      if (payload?.forceRefresh) return true; // Always fetch if forced
      const id = typeof payload === 'object' ? payload.id : payload;
      const { providers } = getState();
      if (providers.loading || (providers.selectedProvider?.id === id && !providers.error)) {
        return false;
      }
    }
  }
);

export const fetchTherapistById = createAsyncThunk(
  'providers/fetchTherapistById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await providerService.getProviderById(id);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
  {
    condition: (id, { getState }) => {
      const { providers } = getState();
      if (providers.loading || (providers.selectedTherapist?.id === id && !providers.error)) {
        return false;
      }
    }
  }
);

export const searchProviders = createAsyncThunk(
  'providers/searchProviders',
  async (params, { rejectWithValue }) => {
    try {
      const response = await providerService.fetchProviders(params);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchContactsForSelection = createAsyncThunk(
  'providers/fetchContactsForSelection',
  async (query, { rejectWithValue }) => {
    try {
      const response = await providerService.fetchProviders(query);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const mergeProviders = createAsyncThunk(
  'providers/mergeProviders',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await providerService.mergeProviders(payload);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


// Slice
const providerSlice = createSlice({
  name: 'providers',
  initialState: {
    providers: [],
    pagination: {
      page: 1,
      limit: 10,
      totalPages: 1,
      totalCount: 0,
    },
    // ---- FIX 1: INITIALIZE STATE FOR THE SELECTION MODAL ----
    // This prevents the "cannot read properties of undefined" error.
    contactList: [],
    contactPagination: null,
    contactListLoading: false,
    // ----
    selectedProvider: null,
    selectedTherapist: null,
    searchResults: [],
    searchPagination: null,
    searchMetadata: null,
    loading: false, // Generic loading for other provider operations
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedProvider: (state) => {
      state.selectedProvider = null;
    },
    clearSelectedTherapist: (state) => {
      state.selectedTherapist = null;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
    },
    // ---- FIX 2: CORRECT THE CLEAR ACTION ----
    // This now targets the correct state properties.
    clearContactList: (state) => {
      state.contactList = [];
      state.contactPagination = null;
      state.contactListLoading = false; // Also reset loading state
    },
  },
  extraReducers: (builder) => {
    builder
      // Cases for general provider management
      .addCase(fetchProviders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProviders.fulfilled, (state, action) => {
        state.loading = false;
        state.providers = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchProviders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(loadProvidersFromCache.fulfilled, (state, action) => {
        state.providers = action.payload;
        state.pagination = {
          ...state.pagination,
          totalCount: action.payload.length,
          totalPages: Math.ceil(action.payload.length / state.pagination.limit)
        };
      })
      .addCase(createProvider.fulfilled, (state, action) => {
        state.providers.unshift(action.payload); // Use unshift to add to the top
        // Mirror to IndexedDB
        entitySyncService.upsertLocalEntity('providers', action.payload);
      })
      .addCase(updateProvider.fulfilled, (state, action) => {
        const index = state.providers.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.providers[index] = action.payload;
        }
        // Mirror to IndexedDB
        entitySyncService.upsertLocalEntity('providers', action.payload);
      })
      .addCase(deleteProvider.fulfilled, (state, action) => {
        state.providers = state.providers.filter(p => p.id !== action.payload);
        // Mirror to IndexedDB
        entitySyncService.deleteLocalEntity('providers', action.payload);
      })
      // Fetch provider by ID
      .addCase(fetchProviderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProviderById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedProvider = action.payload;
      })
      .addCase(fetchProviderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch therapist by ID
      .addCase(fetchTherapistById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTherapistById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTherapist = action.payload;
      })
      .addCase(fetchTherapistById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Search providers
      .addCase(searchProviders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchProviders.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = action.payload.data || action.payload;
        state.searchPagination = action.payload.pagination || null;
        state.searchMetadata = action.payload.metadata || null;
      })
      .addCase(searchProviders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ---- FIX 3: DEDICATED REDUCERS FOR CONTACT SELECTION ----
      .addCase(fetchContactsForSelection.pending, (state) => {
        state.contactListLoading = true; // Use specific loading state
        state.error = null;
      })
      .addCase(fetchContactsForSelection.fulfilled, (state, action) => {
        state.contactListLoading = false;
        // This logic correctly flattens the nested contacts from your API response
        const contacts = (action.payload.data || []).flatMap(firm =>
          (firm.contacts || []).map(contact => ({ ...contact, firm: { id: firm.id, name: firm.name, speciality: firm.speciality, npi: firm.npi, address: firm.address } }))
        );
        state.contactList = contacts;
        state.contactPagination = action.payload.pagination || null;
      })
      .addCase(fetchContactsForSelection.rejected, (state, action) => {
        state.contactListLoading = false;
        state.error = action.payload;
        state.contactList = []; // Clear list on error
      })
      // Merge providers
      .addCase(mergeProviders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(mergeProviders.fulfilled, (state, action) => {
        state.loading = false;
        state.mergeResult = action.payload;
      })
      .addCase(mergeProviders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Export all actions
export const {
  clearError,
  clearSelectedProvider,
  clearSelectedTherapist,
  clearSearchResults,
  clearContactList // Make sure the corrected reducer is exported
} = providerSlice.actions;

export default providerSlice.reducer;